import React from 'react';
import { Key, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

interface Props {
  showApiKey: boolean;
  setShowApiKey: (show: boolean) => void;
  onGenerateApiKey: () => void;
  loading: boolean;
}

const ApiKeysTab: React.FC<Props> = ({
  showApiKey,
  setShowApiKey,
  onGenerateApiKey,
  loading,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
              API Keys
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Manage API keys for programmatic access
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onGenerateApiKey}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Key className="w-4 h-4 mr-2 inline" />
            Generate New Key
          </motion.button>
        </div>

        <div className="space-y-4">
          <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-slate-900 dark:text-slate-100">
                  Production API Key
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Created on {new Date().toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded-full">
                  Active
                </span>
                <button className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm">
                  Revoke
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type={showApiKey ? 'text' : 'password'}
                value="dns_fabric_sk_1234567890abcdef"
                disabled
                className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-mono text-sm"
              />
              <button
                onClick={() => setShowApiKey(!showApiKey)}
                className="px-3 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* API Usage Statistics */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
          API Usage (Last 30 Days)
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">12,543</div>
            <div className="text-sm text-slate-500 dark:text-slate-400">Total Requests</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">99.8%</div>
            <div className="text-sm text-slate-500 dark:text-slate-400">Success Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">24</div>
            <div className="text-sm text-slate-500 dark:text-slate-400">Rate Limited</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysTab;